import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useAuth } from "../../../../context/AuthContext";
import classes from "./AddNonConformingEvents.module.css";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import { env } from "../../../../constants/env";
import QuestionSection from "../../../ui/QuestionSection/QuestionSection";

const questionSets = [
  {
    render: (onAnswerChange, answers, editable) => (
      <>
        <QuestionSection
          number="10.1"
          title="Procedure and/or Process for Handling of Nonconforming work, Nonconformities and Corrective Action"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content:
                "Identification of nonconforming work and nonconformities in any aspect of the laboratory management system;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Documentation of nonconforming work and nonconformities;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Determination of level of risk and evaluation of the impact;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Performing root cause analysis;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Determination of the need for corrective action (how and where);",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Assignment of roles and responsibilities for recalling, resolving and resumption of the nonconforming work and nonconformities;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Determining time frame for resolving nonconforming work and nonconformities;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Implementation of corrective action (including halting examinations and recalling of released results, where applicable);",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Monitoring, reviewing and evaluating the effectiveness of the corrective action taken;",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Retention of records of nonconforming work and nonconformities.",
              number: "j",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="10.2"
          title="Identification and Management of Nonconforming Work and Nonconformities"
          questions={[
            {
              content:
                "Note: Nonconformities should be identified and managed in any aspect of the laboratory management system, including pre-examination, examination, or postexamination processes. Nonconforming examinations or activities occur in many different areas and can be identified in many ways, including clinician complaints, internal quality control indications, instrument calibrations, checking of consumable materials, inter-laboratory comparisons, personnel comments, reporting and certificate checking, laboratory management reviews, and internal and external audits are nonconforming work and nonconformities documented as required below:",
              hideOptions: true,
            },
            {
              content: (
                <>
                  Investigation and determination of the root cause (root cause
                  analysis), and conduct of risk assessment (to determine the
                  level of risk and the need for action);
                  <br />
                  Note: Root cause analysis is a process of identifying and
                  removing the underlying factor of the nonconformance.
                </>
              ),
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: (
                <>
                  Actions (immediate and corrective) taken to control and/or
                  correct the nonconformity or non-conforming work;
                  <br />
                  Note 1: Are examinations halted and results withheld or
                  recalled where the nonconformity compromises patient results?
                  <br />
                  Note 2: Informing the requester where nonconforming
                  work/nonconformity influences the management of the patient
                </>
              ),
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: (
                <>
                  Follow up and review of actions to assess effectiveness
                  <br />
                  Note: Implemented corrective action does not imply
                  effectiveness; therefore, the laboratory must monitor to
                  ensure that the nonconformity has not recurred
                </>
              ),
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="10.3"
          title="Records of Identification and Management of Nonconforming Work and Nonconformities"
          questions={[
            {
              content:
                "Are there records of communication to the requester where nonconforming work or a nonconformity influences the management of the patient?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="10.4"
          title="Resumption of Testing"
          questions={[
            {
              content:
                "Is authorization for the resumption of testing documented (where testing has been halted)?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="10.5"
          title="Corrective Action"
          questions={[
            {
              content:
                "Is corrective action performed and documented for nonconforming work or nonconformities?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
      </>
    ),
  },
];

const AddNonConformingEvents = () => {
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const { user, token } = useAuth();
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const { labId, questionnaireId } = useParams(); // ✅ get questionnaireId too

  const editable = questionnaireId ? status === "Not Attached" : true;

  // --- Prefill answers if editing ---
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!questionnaireId) return; // new form
      try {
        setLoading(true);
        const response = await fetch(
          `${env.kataApiUrl}/api/certification/id/${questionnaireId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to load questionnaire");
        }

        const data = await response.json();

        console.log("DATATAA", data);

        setStatus(data.status);

        const prefilled = {};
        if (data.answers && Array.isArray(data.answers)) {
          data.answers.forEach((ans) => {
            prefilled[ans.questionNumber] = {
              score: ans.score,
              comment: ans.comment,
              points: ans.points,
            };
          });
        }

        setAnswers(prefilled);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [questionnaireId, token]);

  const handleAnswerChange = (questionNumber, score, comment, points) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionNumber]: {
        score,
        comment,
        points: points ? Number(points) : undefined,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const submitAssessment = async () => {
    const answersArray = Object.entries(answers).map(
      ([questionNumber, data]) => ({
        questionNumber,
        score: data.score,
        comment: data.comment,
        points: data.points,
      })
    );

    const payload = {
      name: "SLIPTA",
      labId,
      section: "Non-Conforming Events",
      auditorId: user.id,
      answers: answersArray,
    };

    const url = questionnaireId
      ? `${env.kataApiUrl}/api/certification/id/${questionnaireId}` // edit
      : `${env.kataApiUrl}/api/certification/submit`; // create

    const method = questionnaireId ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Submission failed");
    }

    return await response.json();
  };

  const handleDone = async () => {
    try {
      if (Object.keys(answers).length === 0) {
        toast.warning("Please answer at least one question before submitting");
        return;
      }

      await submitAssessment();

      toast.success(
        questionnaireId ? "updated successfully!" : "Submitted successfully!"
      );

      navigate(-1);
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    }
  };

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">
          Section 10: NonConforming Events Management{" "}
          {questionnaireId && "(Editing)"}
        </h2>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {questionSets[currentSet].render(
            handleAnswerChange,
            answers,
            editable
          )}

          {editable && (
            <CommonFooter
              handlePrevious={handlePrevious}
              handleCancel={() => navigate(-1)}
              handleNext={handleNext}
              handleDone={handleDone}
              questionSets={questionSets}
              currentSet={currentSet}
              cancelText="Cancel"
              doneText={
                questionnaireId ? "Update Assessment" : "Submit Assessment"
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default AddNonConformingEvents;
